import { Stack } from '@mantine/core'
import { FunctionComponent, useState } from 'react'
import Button from '~/components/Button'
import { ConfirmationPhraseField, isPhraseTyped } from '~/components/ConfirmationPhraseField'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { label, prefix } from '~/components/layout/ProductTrackingIntegration'
import { Message } from '~/components/Message'
import { Modal } from '~/components/Modal'

/**
 * One thing about to happen that the user cannot undo alone, and — where the save deserves that much
 * friction — the exact words that let it through.
 *
 * The phrase is always something already on screen — the media's name, the variable just typed — so
 * confirming is a matter of reading what is being changed, not of remembering it.
 *
 * Without a phrase the warning still shows and the save still has to be confirmed; there is simply
 * nothing to type. That is the right weight when the user is creating the thing rather than changing one
 * that already exists: nothing is being taken away, and the choice was made two steps earlier and read
 * back on a review. Typing something back is for the save that turns an existing media into something
 * else under it.
 */
export type SaveConfirmationChallenge = {
  /** Stable across renders: it keys the field, so it must not be the typed value. */
  key: string
  title: string
  description: string
  /** Typed back before the save is allowed through. Omitted where the warning alone is the confirmation. */
  phrase?: string
}

export const isChallengeMet = ({ phrase }: SaveConfirmationChallenge, typed: string | undefined): boolean => !phrase || isPhraseTyped(phrase, typed)

export const areChallengesMet = (challenges: SaveConfirmationChallenge[], typed: Record<string, string>): boolean => challenges.every((challenge) => isChallengeMet(challenge, typed[challenge.key]))

type Props = {
  title: string
  /** Every warning the save carries, each with its own phrase. All of them must be met to confirm. */
  challenges: SaveConfirmationChallenge[]
  confirmLabel: string
  trackingLabel: `${prefix}-${label}`
  onConfirm: () => void | Promise<void>
}

/**
 * The last word before a one-way save.
 *
 * A single modal carries every warning the save triggers rather than one modal each: the global modal
 * is one slot, and closing then reopening it races its own close animation. Two challenges is a real
 * case — activating Node while re-pointing the Prebid variable in the same save.
 */
export const SiteSaveConfirmationModal: FunctionComponent<Props> = ({ title, challenges, confirmLabel, trackingLabel, onConfirm }) => {
  const { close } = useGlobalModal()
  const [typed, setTyped] = useState<Record<string, string>>({})
  const [isSaving, setIsSaving] = useState(false)

  const handleConfirm = async () => {
    if (isSaving) return
    setIsSaving(true)
    // The caller reports its own failures on the form behind, so the modal always steps out of the way.
    await onConfirm()
    await close()
  }

  return (
    <Modal.Content>
      <Modal.Title>{title}</Modal.Title>
      <Modal.Body>
        <Stack gap="1.5rem">
          {challenges.map((challenge) => (
            <Stack key={challenge.key} gap="0.75rem">
              <Message status={'warning'} title={challenge.title} description={challenge.description} />
              {challenge.phrase && (
                <ConfirmationPhraseField
                  phrase={challenge.phrase}
                  value={typed[challenge.key] ?? ''}
                  onChange={(value) => setTyped((previous) => ({ ...previous, [challenge.key]: value }))}
                  disabled={isSaving}
                  trackingLabel={`${trackingLabel}-${challenge.key}-type`}
                />
              )}
            </Stack>
          ))}
        </Stack>
      </Modal.Body>
      <Modal.Actions>
        <Button variant={'tertiary'} onClick={close} disabled={isSaving} trackingLabel={`${trackingLabel}-cancel`}>
          Cancel
        </Button>
        <Button
          disabled={isSaving || !areChallengesMet(challenges, typed)}
          iconName={isSaving ? 'pubstack' : undefined}
          iconSpin={isSaving}
          onClick={handleConfirm}
          trackingLabel={`${trackingLabel}-apply`}
        >
          {confirmLabel}
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}
